import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMultiCart } from '../../Context/MultiCartContext';
import ImageComponent from '../../components/common/Image';
import './SharedCart.css';

const SharedCart = () => {
    const { t } = useTranslation();
    const { shareId } = useParams();
    const navigate = useNavigate();
    const { 
        getSharedCart, 
        loadSharedCart, 
        formatImageUrl, 
        error, 
        setError 
    } = useMultiCart();

    const [sharedCartData, setSharedCartData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingCart, setLoadingCart] = useState(false);

    useEffect(() => {
        const fetchSharedCart = async () => {
            if (!shareId) {
                setError(t('sharedCart.invalid_share_link'));
                setLoading(false);
                return;
            }

            try {
                const result = await getSharedCart(shareId);
                if (result) {
                    setSharedCartData(result);
                } else {
                    setError(t('sharedCart.cart_not_found_or_removed'));
                }
            } catch (err) {
                console.error('Error fetching shared cart:', err);
                setError(t('sharedCart.failed_to_load'));
            } finally {
                setLoading(false);
            }
        };

        fetchSharedCart();
    }, [shareId, getSharedCart, setError, t]);

    const handleLoadCart = async () => {
        if (!shareId) return;

        try {
            setLoadingCart(true);
            const success = await loadSharedCart(shareId);
            if (success) {
                // Redirect to cart page to see the loaded cart
                navigate('/cart');
            } else {
                setError(t('sharedCart.failed_to_load_into_carts'));
            }
        } catch (err) {
            console.error('Error loading shared cart:', err);
            setError(t('sharedCart.failed_to_load'));
        } finally {
            setLoadingCart(false);
        }
    };

    const handleGoToRestaurant = () => {
        if (sharedCartData?.sharedCart?.cart?.restaurantId) {
            navigate(`/restaurant/${sharedCartData.sharedCart.cart.restaurantId}`);
        }
    };

    const calculateTotal = () => {
        if (!sharedCartData?.sharedCart?.cart?.items) return 0;
        return sharedCartData.sharedCart.cart.items.reduce((total, item) => {
            return total + (item.price * item.quantity);
        }, 0);
    };

    if (loading) {
        return (
            <div className="shared-cart-loading">
                <div className="loading-spinner"></div>
                <p>{t('sharedCart.loading')}</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="shared-cart-error">
                <div className="error-icon">❌</div>
                <h2>{t('sharedCart.unable_to_load')}</h2>
                <p>{error}</p>
                <div className="error-actions">
                    <button onClick={() => navigate('/cart')} className="go-to-cart-btn">
                        {t('sharedCart.go_to_my_carts')}
                    </button>
                    <button onClick={() => navigate('/')} className="go-home-btn">
                        {t('sharedCart.go_home')}
                    </button>
                </div>
            </div>
        );
    }

    if (!sharedCartData) {
        return (
            <div className="shared-cart-error">
                <div className="error-icon">🛒</div>
                <h2>{t('sharedCart.cart_not_found')}</h2>
                <p>{t('sharedCart.cart_removed_or_invalid')}</p>
                <div className="error-actions">
                    <button onClick={() => navigate('/')} className="go-home-btn">
                        {t('sharedCart.go_home')}
                    </button>
                </div>
            </div>
        );
    }

    const { sharedCart, sharedBy } = sharedCartData;
    const cart = sharedCart.cart;
    const items = cart.items || [];
    const total = calculateTotal();

    return (
        <div className="shared-cart-page">
            <div className="shared-cart-header">
                <h1>{t('sharedCart.shared_cart')}</h1>
                <p className="shared-by">{t('sharedCart.shared_by')} <strong>{sharedBy}</strong></p>
            </div>

            <div className="shared-cart-container">
                <div className="cart-info">
                    <div className="restaurant-info">
                        <h2>{cart.restaurantName}</h2>
                        <p className="cart-description">{sharedCart.description}</p>
                    </div>
                    
                    <div className="cart-stats">
                        <span className="item-count">{items.length} {t('sharedCart.items')}</span>
                        <span className="total-amount">{total.toLocaleString()} BIF</span>
                    </div>
                </div>

                <div className="cart-items">
                    <h3>{t('sharedCart.items_in_cart')}:</h3>
                    {items.length === 0 ? (
                        <p className="empty-cart">{t('sharedCart.cart_is_empty')}</p>
                    ) : (
                        <div className="items-list">
                            {items.map((item, index) => (
                                <div key={`${item.foodId}-${index}`} className="cart-item">
                                    <div className="item-image">
                                        <ImageComponent
                                            src={formatImageUrl(item.image)}
                                            alt={item.name}
                                            className="food-image"
                                        />
                                    </div>
                                    <div className="item-details">
                                        <h4>{item.name}</h4>
                                        <p className="item-description">{item.description}</p>
                                        <div className="item-pricing">
                                            <span className="quantity">{t('sharedCart.qty')}: {item.quantity}</span>
                                            <span className="price">{item.price.toLocaleString()} BIF {t('sharedCart.each')}</span>
                                            <span className="subtotal">
                                                {t('sharedCart.total')}: {(item.price * item.quantity).toLocaleString()} BIF
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="cart-actions">
                    <button 
                        onClick={handleGoToRestaurant}
                        className="restaurant-btn"
                    >
                        {t('sharedCart.visit_restaurant')}
                    </button>
                    <button 
                        onClick={handleLoadCart}
                        disabled={loadingCart || items.length === 0}
                        className="load-cart-btn"
                    >
                        {loadingCart ? t('sharedCart.loading_cart') : t('sharedCart.add_to_my_carts')}
                    </button>
                </div>

                <div className="cart-summary">
                    <div className="summary-row">
                        <span>{t('sharedCart.subtotal')}:</span>
                        <span>{total.toLocaleString()} BIF</span>
                    </div>
                    <div className="summary-row">
                        <span>{t('sharedCart.delivery_fee')}:</span>
                        <span>1,000 BIF</span>
                    </div>
                    <div className="summary-row total">
                        <span>{t('sharedCart.total')}:</span>
                        <span>{(total + 1000).toLocaleString()} BIF</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SharedCart; 